import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  requestProvision,
  getProvisionStatus,
  isProvisionTerminal,
  type ProvisionRequestResult,
} from '@/lib/api';

/**
 * The in-flight request id, kept in localStorage so a page reload does not orphan a
 * ceremony that is already running. The server's request store is an in-memory Map
 * with no list endpoint, so the id is the only handle back to it — losing it means
 * the operator cannot tell whether their local `claim` landed.
 */
const ACTIVE_ID_KEY = 'maestro.provision.activeId';

function readStoredId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ID_KEY);
  } catch {
    // Private windows and blocked site-data throw on access. A ceremony still works;
    // it just will not survive a reload.
    return null;
  }
}

function writeStoredId(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(ACTIVE_ID_KEY);
    else localStorage.setItem(ACTIVE_ID_KEY, id);
  } catch {
    /* see readStoredId */
  }
}

/**
 * Poll while the ceremony can still change; stop once it cannot.
 *
 * Exported for tests: a panel that keeps polling after a terminal status is a slow
 * leak against an admin-gated route, and one that stops early leaves the operator
 * staring at a stale `pending` after their claim already succeeded.
 */
export function refetchIntervalFor(status: string | undefined): number | false {
  if (status === undefined) return false;
  return isProvisionTerminal(status) ? false : 5_000;
}

const STATUS_COPY: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Waiting for you to run the claim command', tone: 'text-text-secondary' },
  claimed: { label: 'Claim started — provisioning roles', tone: 'text-text-secondary' },
  completed: { label: 'Provisioned', tone: 'text-success' },
  failed: { label: 'Claim reported a failure', tone: 'text-error' },
  expired: { label: 'Expired before the claim ran', tone: 'text-error' },
};

export function ProvisionPanel(): React.ReactElement {
  const [activeId, setActiveId] = useState<string | null>(() => readStoredId());
  const [created, setCreated] = useState<ProvisionRequestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    writeStoredId(activeId);
  }, [activeId]);

  const status = useQuery({
    queryKey: ['provisionStatus', activeId],
    queryFn: () => {
      // `enabled` below stops this running while activeId is null. Narrow with a real
      // check rather than an assertion: the lint config forbids both `as string` and
      // `!` here, and an explicit throw makes the impossible case visible instead of
      // silently coerced.
      if (activeId === null) throw new Error('provision status query ran without an id');
      return getProvisionStatus(activeId);
    },
    enabled: activeId !== null,
    // A 404 means the server restarted and dropped the store. Retrying cannot help.
    retry: false,
    refetchInterval: (query): number | false => refetchIntervalFor(query?.state?.data?.status),
  });

  const m = useMutation({
    mutationFn: requestProvision,
    onMutate: () => {
      setError(null);
      setCreated(null);
    },
    onSuccess: result => {
      setCreated(result);
      setActiveId(result.id);
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  const record = status.data ?? created;
  const currentStatus = record?.status;
  const lost = activeId !== null && status.isError;
  const actionable = currentStatus === 'pending' || currentStatus === 'claimed';

  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Credential Provisioning
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-text-secondary">
          Request the one-time Vault provisioning for the credential registry. You complete it
          locally with your break-glass token — the server never touches Vault admin.
        </p>
        <Button
          onClick={() => {
            m.mutate();
          }}
          disabled={m.isPending}
        >
          {m.isPending ? 'Requesting…' : actionable ? 'Request a new one' : 'Request provisioning'}
        </Button>

        {lost && (
          <p className="mt-3 text-sm text-error">
            Request <code>{activeId}</code> is no longer on the server. Provision requests are held
            in memory, so a restart drops them. Request provisioning again — any roles the earlier
            claim already created are unaffected.
          </p>
        )}

        {record && !lost && (
          <div className="mt-3 space-y-2 text-sm">
            <p className={STATUS_COPY[record.status]?.tone ?? 'text-text-secondary'}>
              {STATUS_COPY[record.status]?.label ?? record.status}
              {actionable && status.isFetching ? ' …' : ''}
            </p>
            <p className="text-text-secondary">
              Roles: {record.plan.map(p => p.roleName).join(', ')}
            </p>
            {actionable && (
              <>
                <p>Run locally with your break-glass VAULT_TOKEN:</p>
                <code className="mt-1 block rounded bg-surface-elevated px-2 py-1 text-xs">
                  bun scripts/vault-ceremony.ts claim --id {record.id}
                </code>
              </>
            )}
            {record.status === 'completed' && (
              <p className="text-text-secondary">
                The writer AppRole was also published to Vault for the cluster to sync. Restart the
                deployment to pick it up.
              </p>
            )}
            {record.status === 'failed' && (
              <p className="text-text-secondary">
                The claim ran but reported a failure — check its output locally. Roles it created
                before failing still exist; re-running the claim is safe.
              </p>
            )}
          </div>
        )}

        {error && <p className="mt-3 text-sm text-error">{error}</p>}
      </CardContent>
    </Card>
  );
}
